import React, { useEffect, useRef } from 'react';

// Seeded, original galaxy artwork. One bounded canvas, no downloaded textures.
export default function NebulaField({ paused, mode }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let seed = 73;
    const random = () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; };
    const stars = Array.from({ length: 210 }, () => ({ x: random(), y: random(), size: .3 + random() * 1.2, phase: random() * 6.28 }));
    const particles = Array.from({ length: 1100 }, (_, i) => {
      const radius = Math.pow(random(), .72);
      const angle = (i % 3) * Math.PI * 2 / 3 + radius * 5.7 + (random() - .5) * .6;
      return { radius, angle, jitter: (random() - .5) * .13, size: .25 + random() * 1.3, warm: random() > .86 };
    });
    let width = 1, height = 1, frame = 0, last = 0, elapsed = 0;
    const pointer = { x: 0, y: 0 };
    const webs = [];
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    const drawConstellation = (now, dark) => {
      while (webs.length && now - webs[0].created > 1500) webs.shift();
      webs.forEach((web) => {
        const life = 1 - (now - web.created) / 1500;
        const pulse = 1 + (1 - life) * .32;
        ctx.save();
        ctx.globalAlpha = Math.max(0, life);
        ctx.strokeStyle = dark ? 'rgba(177,217,255,.78)' : 'rgba(44,93,170,.62)';
        ctx.lineWidth = .8;
        web.points.forEach(([px, py], index) => {
          const nx = web.x + (px - web.x) * pulse;
          const ny = web.y + (py - web.y) * pulse;
          const [nextX, nextY] = web.points[(index + 1) % web.points.length];
          const tx = web.x + (nextX - web.x) * pulse;
          const ty = web.y + (nextY - web.y) * pulse;
          ctx.beginPath(); ctx.moveTo(nx, ny); ctx.lineTo(tx, ty); ctx.lineTo(web.x, web.y); ctx.stroke();
          ctx.fillStyle = dark ? 'rgba(226,244,255,.95)' : 'rgba(40,84,154,.9)'; ctx.beginPath(); ctx.arc(nx, ny, 1.7, 0, Math.PI * 2); ctx.fill();
        });
        ctx.strokeStyle = dark ? 'rgba(205,177,255,.55)' : 'rgba(112,78,184,.42)';
        ctx.beginPath(); ctx.arc(web.x, web.y, 8 + (1 - life) * 48, 0, Math.PI * 2); ctx.stroke();
        ctx.restore();
      });
    };
    const draw = (now = performance.now()) => {
      ctx.clearRect(0, 0, width, height);
      const dark = mode === 'dark';
      const count = width < 700 ? 95 : stars.length;
      for (let i = 0; i < count; i++) {
        const star = stars[i];
        const alpha = (dark ? .3 : .16) + Math.sin(elapsed * .0005 + star.phase) * .12;
        ctx.fillStyle = dark ? `rgba(204,223,255,${alpha})` : `rgba(40,67,136,${alpha})`;
        ctx.beginPath(); ctx.arc(star.x * width + pointer.x * 5, star.y * height + pointer.y * 4, star.size, 0, Math.PI * 2); ctx.fill();
      }
      ctx.save();
      ctx.translate(width * (width < 800 ? .62 : .77) + pointer.x * 8, height * .48 + pointer.y * 5);
      ctx.rotate(-.4);
      ctx.scale(1, .58);
      const radius = Math.min(width * .46, 580);
      const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, radius);
      glow.addColorStop(0, dark ? 'rgba(194,214,255,.2)' : 'rgba(75,114,220,.12)');
      glow.addColorStop(.23, dark ? 'rgba(113,107,220,.13)' : 'rgba(137,117,240,.08)');
      glow.addColorStop(.65, dark ? 'rgba(62,97,205,.055)' : 'rgba(67,148,210,.04)');
      glow.addColorStop(1, 'rgba(65,100,190,0)');
      ctx.fillStyle = glow; ctx.fillRect(-radius, -radius, radius * 2, radius * 2);
      const step = width < 700 ? 2 : 1;
      for (let i = 0; i < particles.length; i += step) {
        const p = particles[i];
        const angle = p.angle + elapsed * .000009;
        const x = Math.cos(angle) * p.radius * radius;
        const y = (Math.sin(angle) * p.radius + p.jitter) * radius;
        const alpha = (1 - p.radius * .65) * (dark ? .6 : .22);
        ctx.fillStyle = p.warm ? `rgba(219,164,192,${alpha})` : dark ? `rgba(163,195,255,${alpha})` : `rgba(49,90,167,${alpha})`;
        ctx.beginPath(); ctx.arc(x, y, p.size, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
      drawConstellation(now, dark);
    };
    const resize = () => {
      width = window.innerWidth; height = window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); draw();
    };
    const tick = time => {
      if (time - last >= 33) { elapsed += Math.min(time - last, 60); last = time; draw(time); }
      frame = requestAnimationFrame(tick);
    };
    const visibility = () => {
      cancelAnimationFrame(frame);
      if (!paused && !document.hidden) { last = performance.now(); frame = requestAnimationFrame(tick); }
    };
    const move = event => {
      if (paused || !fine.matches || event.pointerType === 'touch') return;
      pointer.x = event.clientX / width - .5; pointer.y = event.clientY / height - .5;
    };
    const createWeb = event => {
      if (paused || !fine.matches || event.pointerType === 'touch') return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('a, button, input, textarea, select, dialog, .mobile-menu')) return;
      const pointCount = 7;
      const points = Array.from({ length: pointCount }, (_, index) => {
        const angle = (Math.PI * 2 * index) / pointCount + random() * .35;
        const radius = 34 + random() * 38;
        return [event.clientX + Math.cos(angle) * radius, event.clientY + Math.sin(angle) * radius];
      });
      webs.push({ x: event.clientX, y: event.clientY, points, created: performance.now() });
      if (webs.length > 3) webs.shift();
    };
    resize(); visibility();
    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('pointerdown', createWeb, { passive: true });
    document.addEventListener('visibilitychange', visibility);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', resize); window.removeEventListener('pointermove', move); window.removeEventListener('pointerdown', createWeb); document.removeEventListener('visibilitychange', visibility); };
  }, [paused, mode]);
  return <div className="space-universe" aria-hidden="true"><div className="space-nebula" /><canvas ref={ref} className="space-starfield" /></div>;
}
